import axios from "axios";
import miniSearch from "minisearch";

// The ⌘K help palette in the primary nav. Searches the help-centre guide index
// that scripts/generate-articles-index.js builds at deploy time.

const MIN_QUERY_LENGTH = 3;

// Must match the panel's x-transition:leave duration in help-search.blade.php.
const LEAVE_TRANSITION_MS = 100;

// Same weights the index was tuned against; keep in step with any change to the
// fields in scripts/generate-articles-index.js.
const INDEX_OPTIONS = {
    fields: ["title", "description", "content"],
    storeFields: ["title", "description", "url"],
};

const SEARCH_OPTIONS = {
    boost: { title: 50, description: 10, content: 1 },
    prefix: (term) => term.length > 3,
    fuzzy: (term) => (term.length > 3 ? 0.2 : null),
    combineWith: "AND",
};

function indexPath() {
    let path = "/js/articles-index.json";
    const manifestElement = document.getElementById("mix-manifest");
    if (manifestElement) {
        try {
            const manifest = JSON.parse(manifestElement.innerHTML);
            if (manifest[path]) {
                path = manifest[path];
            }
        } catch (err) {}
    }
    return path;
}

// WordPress renders titles and excerpts with entities (&#8217;) and the odd tag.
// Decode to plain text so the palette can bind with x-text and never x-html.
const decoder = new DOMParser();
function toPlainText(html = "") {
    return decoder.parseFromString(String(html), "text/html").body.textContent.trim();
}

// The app scrolls inside <main>'s overflow-auto pane, not <html>, and the palette is a
// DOM descendant of that pane (it lives in the nav). x-trap.noscroll only locks <html>,
// and `overscroll-behavior` on the fixed wrapper is ignored by Chrome because the
// wrapper has nothing to scroll, so a wheel over the backdrop chained straight through
// to the dashboard. Lock every scrollable ancestor for as long as the palette is open,
// padding for the scrollbar gutter the way the noscroll modifier does for <html>.
// Kept outside the Alpine data object: reactive proxies around DOM nodes are not useful.
let lockedScrollers = [];

function lockScrollAncestors(from) {
    for (let el = from.parentElement; el && el !== document.body; el = el.parentElement) {
        const { overflowY } = getComputedStyle(el);
        if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
            const gutter = el.offsetWidth - el.clientWidth;
            lockedScrollers.push({ el, overflowY: el.style.overflowY, paddingRight: el.style.paddingRight });
            el.style.overflowY = "hidden";
            if (gutter > 0) {
                el.style.paddingRight = `${gutter}px`;
            }
        }
    }
}

// Is one of the app's own modals already on screen? They are page-root Alpine dialogs
// (components/modal/*.blade.php) marked role="dialog" aria-modal and hidden with
// display:none by x-show.
//
// getClientRects(), not offsetParent: these dialogs are position:fixed, and offsetParent
// is null for a fixed element and for everything inside one, so it reads every modal as
// hidden and the guard would never fire.
// Matched by id, not by containment: the palette's own dialog is teleported to <body>, so
// it is no longer a descendant of the component root and a containment test would make it
// see itself and refuse to open.
function anotherDialogIsOpen() {
    return [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')].some(
        (el) => el.id !== "help-search-dialog" && el.getClientRects().length > 0
    );
}

function unlockScrollAncestors() {
    for (const { el, overflowY, paddingRight } of lockedScrollers) {
        el.style.overflowY = overflowY;
        el.style.paddingRight = paddingRight;
    }
    lockedScrollers = [];
}

export default () => ({
    open: false,
    searchTerm: "",
    results: [],
    activeIndex: 0,
    searchIndex: null,
    indexLoaded: false,
    indexLoading: false,
    indexFailed: false,
    pressedOnBackdrop: false,
    os: navigator.platform.includes("Mac") ? "mac" : "windows",

    get query() {
        return this.searchTerm.trim();
    },
    get isQuery() {
        return this.query.length >= MIN_QUERY_LENGTH;
    },
    get hasResults() {
        return this.isQuery && this.results.length > 0;
    },
    get isEmpty() {
        return this.indexLoaded && this.isQuery && this.results.length === 0;
    },
    get isLoading() {
        return this.indexLoading && this.isQuery;
    },
    get activeId() {
        return this.hasResults ? `help-search-option-${this.activeIndex}` : null;
    },

    init() {
        this.$watch("searchTerm", () => this.onSearch());
    },

    openPalette() {
        if (this.open) {
            this.$refs.searchInput?.focus();
            return;
        }
        this.open = true;
        // Suppress the issue screen's single-key triage shortcuts for as long as the
        // palette is up. kbHandle also ignores keys raised from inside an aria-modal
        // dialog, but that test reads the event target, and clicking dead space inside
        // this dialog blurs to <body> without focus-trap correcting it — from there the
        // target test passes and F reaches the instance behind the palette. This flag
        // does not depend on where focus landed.
        window.aaaShortcutsModalOpen = true;
        // $root, not the dialog: the dialog is teleported to <body> and so has no
        // scrollable ancestors left. $root still sits inside <main>'s overflow-auto pane,
        // which is the one that must be held still.
        lockScrollAncestors(this.$root);
        this.fetchIndex(true);
        this.$nextTick(() => this.$refs.searchInput?.focus());
    },

    closePalette() {
        if (!this.open) {
            return;
        }
        this.open = false;
        window.aaaShortcutsModalOpen = false;
        unlockScrollAncestors();
        this.searchTerm = "";
        this.results = [];
        this.activeIndex = 0;
        this.restoreFocusToTrigger();
    },

    // Focus goes back to the trigger, but only after x-trap lets go. The panel takes 100ms
    // to leave, and for that whole window the trap is still live and yanks any outside
    // focus straight back in, so focusing on $nextTick left focus stranded on the search
    // input inside a display:none dialog. Wait for the leave transition, with a timer for
    // the cases where transitionend never fires: reduced motion, or a backgrounded tab
    // whose rAF-driven transitions do not run.
    restoreFocusToTrigger() {
        const trigger = this.$refs.trigger;
        if (!trigger) {
            return;
        }

        let restored = false;
        const focusTrigger = () => {
            if (!restored) {
                restored = true;
                trigger.focus();
            }
        };

        this.$refs.panel?.addEventListener("transitionend", focusTrigger, { once: true });
        setTimeout(focusTrigger, LEAVE_TRANSITION_MS + 50);
    },

    // ⌘K / Ctrl+K anywhere on the page. Bound on the window from the blade.
    //
    // Declines while another modal owns the screen. The palette shares z-[60] with the
    // app's modals and is later in the DOM only sometimes, so opening underneath one puts
    // an invisible focused input behind a backdrop and Escape then closes both at once.
    shortcut(event) {
        // The chord has to match the one the trigger advertises. Accepting ctrl on a Mac
        // stole Ctrl+K, which macOS gives to "delete to end of line", so typing it in a
        // comment box opened the palette and pulled focus out of the sentence.
        const chord = this.os === "mac" ? event.metaKey : event.ctrlKey;

        if (!chord || (event.key || "").toLowerCase() !== "k") {
            return;
        }
        if (!this.open && anotherDialogIsOpen()) {
            return;
        }

        event.preventDefault();
        this.openPalette();
    },

    // One download per page load. Before this guard every keystroke typed while
    // the (now 264 KB) index was still in flight started another request.
    // `force` is the retry the user asked for by opening the palette again. Without it a
    // failed fetch would be retried from onSearch() on every keystroke — eight requests
    // for an eight-letter word, each one flickering the failed panel away and back.
    fetchIndex(force = false) {
        if (this.indexLoaded || this.indexLoading || (this.indexFailed && !force)) {
            return;
        }
        this.indexLoading = true;
        this.indexFailed = false;

        axios
            .get(indexPath())
            .then((resp) => {
                this.searchIndex = miniSearch.loadJS(resp.data, INDEX_OPTIONS);
                this.indexLoaded = true;
                this.onSearch();
            })
            .catch(() => {
                this.indexFailed = true;
            })
            .finally(() => {
                this.indexLoading = false;
            });
    },

    onSearch() {
        if (!this.indexLoaded) {
            this.fetchIndex();
            return;
        }

        this.results = this.isQuery
            ? this.searchIndex.search(this.query, SEARCH_OPTIONS).map((hit) => ({
                  id: hit.id,
                  url: hit.url,
                  title: toPlainText(hit.title),
                  description: toPlainText(hit.description),
              }))
            : [];

        // Back to the top with the highlight. The list keeps its scrollTop across an
        // x-for re-render, so refining a query the user had scrolled down through would
        // otherwise put the active row — and whatever Enter opens — above the fold.
        this.activeIndex = 0;
        this.$nextTick(() => {
            if (this.$refs.resultsPane) {
                this.$refs.resultsPane.scrollTop = 0;
            }
        });
    },

    setActive(index) {
        this.activeIndex = index;
    },

    // Click-away, but only for a click that both started and ended on the empty area
    // around the panel. A `click.self` alone also fires when a text selection drag starts
    // inside the input and releases outside it, which threw away the query mid-gesture.
    backdropMouseDown(event) {
        this.pressedOnBackdrop = event.target === event.currentTarget;
    },

    backdropClick(event) {
        if (this.pressedOnBackdrop && event.target === event.currentTarget) {
            this.closePalette();
        }
        this.pressedOnBackdrop = false;
    },

    moveActive(delta) {
        if (!this.hasResults) {
            return;
        }
        const count = this.results.length;
        this.activeIndex = (this.activeIndex + delta + count) % count;
        this.$nextTick(() => {
            document.getElementById(this.activeId)?.scrollIntoView({ block: "nearest" });
        });
    },

    openActive() {
        if (!this.hasResults) {
            return;
        }
        document.getElementById(this.activeId)?.querySelector("a")?.click();
    },

    // The only live region on the screen, so it owes the user a word for every state that
    // arrives after their keystroke. Returning "" while the index loads or after it fails
    // announced nothing on exactly the two the user cannot see coming.
    resultCountLabel(one, many, none, loading, failed) {
        if (this.indexFailed) {
            return failed;
        }
        if (this.indexLoading) {
            return loading;
        }
        if (!this.isQuery || !this.indexLoaded) {
            return "";
        }

        const count = this.results.length;
        if (count === 0) {
            return none;
        }

        return count === 1 ? one : many.replace(":count", count);
    },
});
